import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import axios from 'axios';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccountPage from './pages/AccountPage';
import TradeApplicationPage from './pages/TradeApplicationPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import EstimateApproval from './pages/EstimateApproval';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import MaintenancePage from './pages/MaintenancePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminTradeApps from './pages/admin/AdminTradeApps';
import AdminInventory from './pages/admin/AdminInventory';
import AdminProjects from './pages/admin/AdminProjects';
import AdminPageBuilder from './pages/admin/AdminPageBuilder';
import AdminSuppliers from './pages/admin/AdminSuppliers';
import AdminPurchaseOrders from './pages/admin/AdminPurchaseOrders';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminStockMovements from './pages/admin/AdminStockMovements';
import AdminActivityLog from './pages/admin/AdminActivityLog';
import AdminEmailTemplates from './pages/admin/AdminEmailTemplates';
import AdminEstimates from './pages/admin/AdminEstimates';
import AdminEmailLogs from './pages/admin/AdminEmailLogs';
import AdminSocialMedia from './pages/admin/AdminSocialMedia';
import AdminEmailSettings from './pages/admin/AdminEmailSettings';
import AdminCreditAccounts from './pages/admin/AdminCreditAccounts';
import AdminSiteImages from './pages/admin/AdminSiteImages';
import AdminNavigation from './pages/admin/AdminNavigation';
import AdminXero from './pages/admin/AdminXero';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminShipping from './pages/admin/AdminShipping';
import AdminTickets from './pages/admin/AdminTickets';
import TicketDetail from './pages/admin/TicketDetail';
import AdminBackorders from './pages/admin/AdminBackorders';
import AdminStoreSettings from './pages/admin/AdminStoreSettings';
import AdminBackups from './pages/admin/AdminBackups';
import AdminPaymentMethods from './pages/admin/AdminPaymentMethods';
import AdminVendors from './pages/admin/AdminVendors';
import AdminSecurity from './pages/admin/AdminSecurity';
import AdminLabelDesigner from './pages/admin/AdminLabelDesigner';
import VendorsPage from './pages/VendorsPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ConfirmEmailChangePage from './pages/ConfirmEmailChangePage';
import VendorDetailPage from './pages/VendorDetailPage';
import SupportTicketPage from './pages/SupportTicketPage';

// Context
export const AuthContext = createContext(null);
export const ThemeContext = createContext(null);
export const CartContext = createContext(null);
export const MaintenanceContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    // Check if 2FA is required
    if (res.data.requires_2fa) {
      return { requires_2fa: true, temp_token: res.data.temp_token, user_email: res.data.user_email };
    }
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const complete2FA = async (tempToken, code) => {
    const res = await axios.post(`${API}/auth/2fa/authenticate`, { temp_token: tempToken, code });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, complete2FA, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Provider
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Cart Provider
export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [loading, setLoading] = useState(false);
  const { token } = useContext(AuthContext);

  const fetchCart = async () => {
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setCart(res.data);
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchCart();
  }, [token]);

  const addToCart = async (productId, quantity = 1) => {
    await axios.post(`${API}/cart/add`, { product_id: productId, quantity });
    await fetchCart();
  };

  const updateCartItem = async (productId, quantity) => {
    await axios.put(`${API}/cart/update`, { product_id: productId, quantity });
    await fetchCart();
  };

  const removeFromCart = async (productId) => {
    await axios.delete(`${API}/cart/remove/${productId}`);
    await fetchCart();
  };

  const clearCart = async () => {
    await axios.delete(`${API}/cart/clear`);
    setCart({ items: [], subtotal: 0 });
  };

  return (
    <CartContext.Provider value={{ cart, loading, fetchCart, addToCart, updateCartItem, removeFromCart, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

// Protected Route - Must be used inside Router context
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="text-muted-foreground">Loading...</div></div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" />;

  return children;
};

// Maintenance Gate - blocks customer-facing pages when maintenance is on (unless IP whitelisted)
const MaintenanceGate = ({ children }) => {
  const [maintenance, setMaintenance] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/maintenance-status`);
        setMaintenance(res.data);
      } catch {
        setMaintenance({ maintenance: false });
      }
    };
    check();
  }, [location.pathname]);

  // Always allow admin routes and login
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginRoute = location.pathname === '/login';
  if (isAdminRoute || isLoginRoute) return children;

  // Still loading
  if (maintenance === null) return <div className="min-h-screen bg-[#0a0a0a]" />;

  // Maintenance active and not whitelisted
  if (maintenance.maintenance && !maintenance.is_whitelisted) {
    return <MaintenancePage message={maintenance.message} />;
  }

  return children;
};

function AppContent() {
  return (
    <CartProvider>
      <MaintenanceGate>
        <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:slug" element={<ProductDetailPage />} />
        <Route path="/category/:slug" element={<ProductsPage />} />
        <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
        <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
        <Route path="/checkout/cancel" element={<Navigate to="/cart" />} />
        <Route path="/order-confirmation/:orderId" element={<ProtectedRoute><OrderConfirmationPage /></ProtectedRoute>} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
        <Route path="/support/:ticketId" element={<ProtectedRoute><SupportTicketPage /></ProtectedRoute>} />
        <Route path="/trade-application" element={<TradeApplicationPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:slug" element={<ProjectDetailPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/vendors/:slug" element={<VendorDetailPage />} />
        
        {/* Public Estimate Approval */}
        <Route path="/estimate/approve/:estimateId" element={<EstimateApproval />} />
        
        {/* Email Verification Routes */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/products" element={<ProtectedRoute adminOnly><AdminProducts /></ProtectedRoute>} />
        <Route path="/admin/orders" element={<ProtectedRoute adminOnly><AdminOrders /></ProtectedRoute>} />
        <Route path="/admin/transactions" element={<ProtectedRoute adminOnly><AdminTransactions /></ProtectedRoute>} />
        <Route path="/admin/users" element={<Navigate to="/admin/customers" />} />
        <Route path="/admin/trade-applications" element={<ProtectedRoute adminOnly><AdminTradeApps /></ProtectedRoute>} />
        <Route path="/admin/inventory" element={<Navigate to="/admin/products" />} />
        <Route path="/admin/suppliers" element={<ProtectedRoute adminOnly><AdminSuppliers /></ProtectedRoute>} />
        <Route path="/admin/purchase-orders" element={<ProtectedRoute adminOnly><AdminPurchaseOrders /></ProtectedRoute>} />
        <Route path="/admin/stock-movements" element={<ProtectedRoute adminOnly><AdminStockMovements /></ProtectedRoute>} />
        <Route path="/admin/customers" element={<ProtectedRoute adminOnly><AdminCustomers /></ProtectedRoute>} />
        <Route path="/admin/invoices" element={<ProtectedRoute adminOnly><AdminInvoices /></ProtectedRoute>} />
        <Route path="/admin/estimates" element={<ProtectedRoute adminOnly><AdminEstimates /></ProtectedRoute>} />
        <Route path="/admin/projects" element={<ProtectedRoute adminOnly><AdminProjects /></ProtectedRoute>} />
        <Route path="/admin/pages" element={<ProtectedRoute adminOnly><AdminPageBuilder /></ProtectedRoute>} />
        <Route path="/admin/activity-log" element={<ProtectedRoute adminOnly><AdminActivityLog /></ProtectedRoute>} />
        <Route path="/admin/email-templates" element={<ProtectedRoute adminOnly><AdminEmailTemplates /></ProtectedRoute>} />
        <Route path="/admin/email-logs" element={<ProtectedRoute adminOnly><AdminEmailLogs /></ProtectedRoute>} />
        <Route path="/admin/email-settings" element={<ProtectedRoute adminOnly><AdminEmailSettings /></ProtectedRoute>} />
        <Route path="/admin/credit-accounts" element={<ProtectedRoute adminOnly><AdminCreditAccounts /></ProtectedRoute>} />
        <Route path="/admin/social-media" element={<ProtectedRoute adminOnly><AdminSocialMedia /></ProtectedRoute>} />
        <Route path="/admin/site-images" element={<ProtectedRoute adminOnly><AdminSiteImages /></ProtectedRoute>} />
        <Route path="/admin/navigation" element={<ProtectedRoute adminOnly><AdminNavigation /></ProtectedRoute>} />
        <Route path="/admin/store-settings" element={<ProtectedRoute adminOnly><AdminStoreSettings /></ProtectedRoute>} />
        <Route path="/admin/payment-methods" element={<ProtectedRoute adminOnly><AdminPaymentMethods /></ProtectedRoute>} />
        <Route path="/admin/backups" element={<ProtectedRoute adminOnly><AdminBackups /></ProtectedRoute>} />
        <Route path="/admin/xero" element={<ProtectedRoute adminOnly><AdminXero /></ProtectedRoute>} />
        <Route path="/admin/shipping" element={<ProtectedRoute adminOnly><AdminShipping /></ProtectedRoute>} />
        <Route path="/admin/tickets" element={<ProtectedRoute adminOnly><AdminTickets /></ProtectedRoute>} />
        <Route path="/admin/tickets/:ticketId" element={<ProtectedRoute adminOnly><TicketDetail /></ProtectedRoute>} />
        <Route path="/admin/backorders" element={<ProtectedRoute adminOnly><AdminBackorders /></ProtectedRoute>} />
        <Route path="/admin/vendors" element={<ProtectedRoute adminOnly><AdminVendors /></ProtectedRoute>} />
        <Route path="/admin/security" element={<ProtectedRoute adminOnly><AdminSecurity /></ProtectedRoute>} />
        <Route path="/admin/label-designer" element={<ProtectedRoute adminOnly><AdminLabelDesigner /></ProtectedRoute>} />
      </Routes>
      </MaintenanceGate>
      <Toaster position="top-right" />
    </CartProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
